import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './EventDetailView.css';

function EventDetailView({ event, onClose }) {
  const [classNames, setClassNames] = useState({});

  const loadClassNames = useCallback(async () => {
    if (!event.participatingClassIds || event.participatingClassIds.length === 0) return;

    const names = {};
    for (const classId of event.participatingClassIds) {
      try {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        if (classDoc.exists()) {
          const classData = classDoc.data();
          names[classId] = classData.name || classData.className || classId;
        } else {
          names[classId] = classId;
        }
      } catch (error) {
        console.error('Error loading class name:', error);
        names[classId] = classId;
      }
    }
    setClassNames(names);
  }, [event]);

  useEffect(() => {
    loadClassNames();
  }, [loadClassNames]);

  const hasLocation = () => {
    return event.location && event.location.latitude && event.location.longitude &&
      event.location.latitude !== 0 && event.location.longitude !== 0;
  };

  const getMapUrl = () => {
    if (hasLocation()) {
      const { latitude, longitude } = event.location;
      return `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
    }
    if (event.location?.address) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(event.location.address)}&z=13&output=embed`;
    }
    return null;
  };

  const openAppleMaps = () => {
    if (hasLocation()) {
      const { latitude, longitude } = event.location;
      window.open(`https://maps.apple.com/?ll=${latitude},${longitude}&q=${encodeURIComponent(event.name || 'Event')}`, '_blank');
    } else if (event.location && event.location.address) {
      const address = encodeURIComponent(event.location.address);
      window.open(`https://maps.apple.com/?q=${address}&dirflg=d`, '_blank');
    }
  };

  const openGoogleMaps = () => {
    if (hasLocation()) {
      const { latitude, longitude } = event.location;
      window.open(`https://maps.google.com/?q=${latitude},${longitude}`, '_blank');
    } else if (event.location && event.location.address) {
      const address = encodeURIComponent(event.location.address);
      window.open(`https://maps.google.com/?q=${address}&dirflg=d`, '_blank');
    }
  };

  const getDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  return (
    <div className="event-detail-overlay" onClick={onClose}>
      <div className="event-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="event-detail-header">
          <h2>{event.name}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="event-detail-content">
          <div className="event-detail-section">
            <h3>Event Details</h3>
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value">{event.eventType}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className={`detail-value status ${event.status?.toLowerCase()}`}>
                {event.status}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Date</span>
              <span className="detail-value">{getDate(event.date)}</span>
            </div>
            {event.description && (
              <div className="detail-row description">
                <span className="detail-label">Description</span>
                <span className="detail-value">{event.description}</span>
              </div>
            )}
            {event.endDate && (
              <div className="detail-row">
                <span className="detail-label">End Date</span>
                <span className="detail-value">{getDate(event.endDate)}</span>
              </div>
            )}
            {event.hasTickets && (
              <div className="detail-row">
                <span className="detail-label">Tickets</span>
                <span className="detail-value">
                  {event.ticketPrice > 0
                    ? `${event.ticketCurrency || 'XCD'} ${event.ticketPrice.toFixed(2)}`
                    : 'Free'}
                  {event.maxTickets > 0 && ` (Max: ${event.maxTickets})`}
                </span>
              </div>
            )}
            {event.contactInfo && (
              <div className="detail-row">
                <span className="detail-label">Contact</span>
                <span className="detail-value">{event.contactInfo}</span>
              </div>
            )}
          </div>

          {event.location && (event.location.address || hasLocation()) && (
            <div className="event-detail-section">
              <h3>Location</h3>
              {event.location.address && (
                <div className="location-info">
                  <div className="location-icon">📍</div>
                  <div className="location-address">{event.location.address}</div>
                </div>
              )}
              
              {/* Map Display */}
              {getMapUrl() && (
                <div className="map-container">
                  <iframe
                    width="100%"
                    height="200"
                    frameBorder="0"
                    style={{ border: 0, borderRadius: '10px' }}
                    src={getMapUrl()}
                    allowFullScreen
                    title="Event Location Map"
                  />
                </div>
              )}
              
              {/* Direction Buttons */}
              <div className="directions-buttons">
                <button className="directions-button apple-maps" onClick={openAppleMaps}>
                  <span className="directions-icon">🍎</span>
                  <span>Apple Maps</span>
                </button>
                <button className="directions-button google-maps" onClick={openGoogleMaps}>
                  <span className="directions-icon">🗺️</span>
                  <span>Google Maps</span>
                </button>
              </div>
            </div>
          )}

          {event.participatingClassIds && event.participatingClassIds.length > 0 && (
            <div className="event-detail-section">
              <h3>Participating Classes</h3>
              <div className="classes-list">
                {event.participatingClassIds.map((classId, index) => (
                  <div key={index} className="class-tag">
                    {classNames[classId] || 'Loading...'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventDetailView;
